import type { ContentItem, Title } from '@/types'
import { getTitleCustomData } from '@/utils/helpers'

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function asPositiveNumber(value: unknown): number | null {
  if (typeof value === 'boolean') return value ? 18 : null
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    if (Number.isFinite(parsed) && parsed > 0) return parsed
  }
  return null
}

function readCustomValue(
  title: Title | ContentItem | null | undefined,
  keys: string[],
): unknown {
  if (!title || typeof title !== 'object') return undefined

  const customData = getTitleCustomData(title)
  const record = title as Record<string, unknown>

  for (const key of keys) {
    if (customData[key] !== undefined && customData[key] !== null) {
      return customData[key]
    }
  }

  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null) {
      return record[key]
    }
  }

  return undefined
}

export function getViewerMessage(title: Title | ContentItem | null | undefined): string | null {
  return asNonEmptyString(readCustomValue(title, ['viewerMessage', 'viewer_message']))
}

export function getViewerWarnMessage(title: Title | ContentItem | null | undefined): string | null {
  return asNonEmptyString(readCustomValue(title, ['viewerWarnMessage', 'viewer_warn_message']))
}

/** Minimum age required when `customData.ageVerificationRequired` is set. */
export function getAgeVerificationMinAge(
  title: Title | ContentItem | null | undefined,
): number | null {
  return asPositiveNumber(
    readCustomValue(title, ['ageVerificationRequired', 'age_verification_required']),
  )
}

export function isAgeVerificationRequired(
  title: Title | ContentItem | null | undefined,
): boolean {
  return getAgeVerificationMinAge(title) != null
}

export function getAgeFromDateOfBirth(
  dateOfBirth: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!dateOfBirth) return null

  const birth = new Date(dateOfBirth)
  if (Number.isNaN(birth.getTime())) return null

  let age = now.getFullYear() - birth.getFullYear()
  const monthDelta = now.getMonth() - birth.getMonth()
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < birth.getDate())) {
    age -= 1
  }

  return age >= 0 ? age : null
}

export type AgeVerificationResult =
  | { required: false }
  | { required: true; minAge: number; allowed: true }
  | { required: true; minAge: number; allowed: false; reason: 'missing-dob' | 'underage' }

export function evaluateAgeVerification(
  title: Title | ContentItem | null | undefined,
  dateOfBirth: string | null | undefined,
  now: Date = new Date(),
): AgeVerificationResult {
  const minAge = getAgeVerificationMinAge(title)
  if (minAge == null) return { required: false }

  const age = getAgeFromDateOfBirth(dateOfBirth, now)
  if (age == null) {
    return { required: true, minAge, allowed: false, reason: 'missing-dob' }
  }

  if (age < minAge) {
    return { required: true, minAge, allowed: false, reason: 'underage' }
  }

  return { required: true, minAge, allowed: true }
}
